import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './ExternalLinkLandingPage.css';
import config from '../config';

const ExternalLinkLandingPage = () => {
  const [platform, setPlatform] = useState('unknown');
  const location = useLocation();
  const hasAttemptedRedirect = useRef(false);
  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    // Get the full current URL for QR code
    const fullUrl = window.location.href;
    setCurrentUrl(fullUrl);
  }, []);

  const detectPlatform = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for iPad specifically - this handles both traditional and modern iPad user agents
    const isIpad = /iPad/i.test(userAgent) || 
                  (navigator.platform === 'MacIntel' && 
                   navigator.maxTouchPoints > 1 && 
                   !window.MSStream);
                   
    if (isIpad || /iPhone|iPod/i.test(userAgent) && !window.MSStream) {
      setPlatform('ios');
    } else if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else if (/Windows NT|Mac OS X|Linux/.test(userAgent)) {
      setPlatform('desktop');
    } else {
      setPlatform('unknown');
    }
  };

  const getAppStoreUrl = () => {
    switch (platform) {
      case 'ios':
        return 'https://apps.apple.com/app//dream-book-3d/id6472164721';
      case 'android':
        return 'https://play.google.com/store/apps/details?id=com.dreambookLLC.dreambook3D';
      case 'desktop':
        return 'https://apps.microsoft.com/detail/9mx8lk1t4p8s';
      default:
        return '/';
    }
  };

  useEffect(() => {
    console.log("Effect running. location.search:", location.search);

    // Detect platform directly here or ensure it runs before this effect
    // For debugging, let's call it here to ensure it's fresh
    detectPlatform();

    const currentParams = new URLSearchParams(location.search);
    const currentModelParam = currentParams.get('model');
    const currentDesignParam = currentParams.get('design');

    const currentReferralParam = currentParams.get('referral');
    const appScheme = 'dreambook3d://';
    let calculatedDeepLinkUrl;

    if (currentModelParam) 
      {
      calculatedDeepLinkUrl = `${appScheme}scene?model=${encodeURIComponent(currentModelParam)}`;
      if (currentReferralParam) {
        calculatedDeepLinkUrl += `&referral=${encodeURIComponent(currentReferralParam)}`;
      }
    }
    else if (currentDesignParam)
    {
      console.log("Design parameter detected:", currentDesignParam);
      
      // Create a proper URL object for the deep link to ensure correct encoding
      const deepLinkURL = new URL(`${appScheme}scene`); 
      deepLinkURL.searchParams.append('design', encodeURIComponent(currentDesignParam));
      
      // Get the properly formatted URL string
      // Use toString() and then replace the protocol part if needed
      calculatedDeepLinkUrl = deepLinkURL.toString();
      
      // If URL object adds http:// prefix (which it might), replace it with the app scheme
      if (calculatedDeepLinkUrl.startsWith('http://') || calculatedDeepLinkUrl.startsWith('https://')) {
        calculatedDeepLinkUrl = calculatedDeepLinkUrl.replace(/^https?:\/\//, appScheme);
      }
      
      console.log("Generated deep link:", calculatedDeepLinkUrl);
    }
    else 
    {
      const genericParams = new URLSearchParams();
      currentParams.forEach((value, key) => {
        if (key !== 'model') 
          {
          genericParams.append(key, value);
        }
      });
      const genericParamsString = genericParams.toString();
      calculatedDeepLinkUrl = `${appScheme}${genericParamsString ? '?' + genericParamsString : ''}`;
    }

    const shouldRedirect = true; //currentModelParam || currentParams.toString().length > 0;

    if (platform !== 'unknown' && shouldRedirect && !hasAttemptedRedirect.current) {
      console.log(`Attempting redirect: shouldRedirect=${shouldRedirect}, hasAttempted=${hasAttemptedRedirect.current}`);
      hasAttemptedRedirect.current = true;

      console.log("Platform state before redirect check:", platform);

      if (platform === 'ios' || platform === 'android') {
        console.log("Attempting mobile deep link:", calculatedDeepLinkUrl);
        window.location.href = calculatedDeepLinkUrl;
      } else if (platform === 'desktop') {

        if (currentParams.has('dev') && currentParams.get('dev').toLowerCase() !== 'false') {
          console.log("Platform is desktop. Original URL:", calculatedDeepLinkUrl);
          // Store necessary parameters for ReturnToAppPage
          const desktopUrl = '127.0.0.1:5662';
          const desktopPath = calculatedDeepLinkUrl.replace(appScheme, '');
          
          // Store the redirect information in localStorage
          localStorage.setItem('redirectUrl', desktopUrl);
          localStorage.setItem('redirectPath', desktopPath);
          
          console.log("Redirecting to ReturnToAppPage for desktop app");
          // Navigate to the ReturnToAppPage which will handle the redirect
          window.location.href = '/return-to-app';
        }
        else{
          window.location.href = calculatedDeepLinkUrl;

        }
        return;
        
      }
      // No need for else block since we already checked platform !== 'unknown'
    } else if (!shouldRedirect) {
      console.log("Resetting redirect flag because shouldRedirect is false.");
      hasAttemptedRedirect.current = false;
    } else {
      console.log(`Skipping redirect: platform=${platform}, shouldRedirect=${shouldRedirect}, hasAttempted=${hasAttemptedRedirect.current}`);
    }

  }, [location.search, platform]);

  const handleDownloadApp = () => {
    const url = getAppStoreUrl();
    if (url && url !== '/') {
      window.open(url, '_blank');
    } else {
      console.warn('No app store URL available for this platform.');
    }
  };

  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  return (
    <div className="external-link-container">
      <div className="logo-container">
        <img 
          src="/images/dream-book-logo-dark.png" 
          alt="Dreambook Logo" 
          className="logo-image" 
        />
      </div>

      <div className="content-card">

        {platform === 'ios' || platform === 'android' ? (
          <>
            <h2 className="app-title">Welcome to Dream Book 3D</h2>
            
            {(new URLSearchParams(location.search)).get('model') ? (
              <div className="mb-6">
                <p className="app-message font-medium">
                  Experience this content in the Dream Book app
                </p>
                <p className="text-gray-500 mt-2 text-sm">
                  Get the Dream Book 3D app to view and start designing this home in 3D
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <p className="app-message font-medium">
                  Get the Dream Book app
                </p>
                <p className="text-gray-500 mt-2 text-sm">
                Download the Dream Book 3D app now to start designing your dream home
                </p>
              </div>
            )}
          </>
        ) : null}

        {/* Display parameters if present */}
        {/* <div className="mb-6">
          {(new URLSearchParams(location.search)).get('referral') && (
            <p className="text-xs text-gray-500 mb-1">
              <span className="font-medium">Referral:</span> {(new URLSearchParams(location.search)).get('referral')}
            </p>
          )}
          {(new URLSearchParams(location.search)).get('model') && (
            <p className="text-xs text-gray-500">
              <span className="font-medium">Model:</span> {(new URLSearchParams(location.search)).get('model')}
            </p>
          )}
        </div> */}

        {/* App store section */}
        <div className="space-y-6">
          {(platform === 'ios' || platform === 'android') && (
            <div>
              {platform === 'ios' && (
                <a
                  href={getAppStoreUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full"
                  aria-label="Download Dream Book on the App Store"
                >
                  <img 
                    src="/images/appstore.svg" 
                    alt="Download on the App Store" 
                    className="store-image" 
                  />
                </a>
              )}

              {platform === 'android' && (
                <a
                  href={getAppStoreUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full"
                  aria-label="Get Dream Book on Google Play"
                >
                  <img 
                    src="/images/playstore.svg" 
                    alt="Get it on Google Play" 
                    className="store-image" 
                  />
                </a>
              )}
            </div>
          )}
          
          {/* Desktop message - only shown in development environment */}
          {platform === 'desktop' && (
            <div className="flex flex-col items-center">
              <div className="text-center mb-6">
                <h3 className="text-xl font-medium text-gray-800 mb-2">Scan to Experience dream book 3D</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Scan this QR code with your mobile device to view this content in the dream book 3D app
                </p>
                {/* Larger QR code section */}
                <div className="mb-6 p-4 bg-white rounded-lg shadow-sm inline-block">
                  <QRCodeSVG value={currentUrl} size={180} />
                </div>
              </div>
              
              {/* App store links at the bottom */}
              <div className="mt-4 text-center">
                <p className="text-gray-600 text-sm mb-4">Don't have the app yet? Download it here:</p>
                <div className="flex flex-col items-center gap-4">
                  <div className="flex flex-row justify-center items-center gap-4">
                    <a
                      href="https://apps.apple.com/app//dream-book-3d/id6472164721"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Download Dream Book on the App Store"
                      tabIndex="0"
                      onKeyDown={(event) => handleKeyDown(event, () => window.open("https://apps.apple.com/app//dream-book-3d/id6472164721", "_blank"))}
                    >
                      <img 
                        src="/images/appstore.svg" 
                        alt="Download on the App Store" 
                        className="h-10 transition-transform hover:scale-105" 
                      />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.dreambookLLC.dreambook3D"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Get Dream Book on Google Play"
                      tabIndex="0"
                      onKeyDown={(event) => handleKeyDown(event, () => window.open("https://play.google.com/store/apps/details?id=com.dreambookLLC.dreambook3D", "_blank"))}
                    >
                      <img 
                        src="/images/playstore.svg" 
                        alt="Get it on Google Play" 
                        className="h-10 transition-transform hover:scale-105" 
                      />
                    </a>
                  </div>
                  <a
                    href={getAppStoreUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get Dream Book on Microsoft Store"
                    tabIndex="0"
                    className="transition-transform hover:scale-105"
                    onKeyDown={(event) => handleKeyDown(event, () => window.open(getAppStoreUrl(), "_blank"))}
                  >
                    <img 
                      src="/images/microsoftstore.svg"
                      alt="Get it from Microsoft Store"
                      style={{ maxWidth: '220px', height: 'auto' }}
                    />
                  </a>
                </div>
              </div>
            </div>
          )}
          
          {/* Unknown platform message */}
          {platform === 'unknown' && (
            <p className="text-gray-500 pt-4 text-sm">
              Please visit the App Store or Google Play to download the Dream Book app.
            </p>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <div className="footer">
        <p>Copyright 2025 dream book, LLC. All rights reserved.</p>
      </div>
    </div>
  );

};

export default ExternalLinkLandingPage;
